import Filter from '@components/Filter';
import { useFormContext } from 'react-hook-form';
import { GeneralTranslationLanguages, ProjectTranslationLanguages, TranslationSetupForm } from '@customTypes';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { Box, Button, Tooltip } from '@mui/material';
import { PlusIcon } from '@heroicons/react/24/outline';
import { FC, useMemo, useState } from 'react';
import { convertToOptions } from '@utils/helpers';
import { EMPTY_STRING } from '@constants';
import TooltipIcon from '@components/TooltipIcon';

import styles from './TranslationSetupDialog.module.scss';
import { AddLanguageDialog } from './AddLanguageDialog';

interface LanguageControlsProps {
  data: GeneralTranslationLanguages | ProjectTranslationLanguages;
  hasChanges: boolean;
  onAddClick: () => void;
}

export const LanguageControls: FC<LanguageControlsProps> = ({ data, onAddClick, hasChanges }) => {
  const { getTranslation } = useCustomTranslation();
  const { control } = useFormContext<TranslationSetupForm>();
  const [isAddNewLanguageDialogOpen, setIsAddNewLanguageDialogOpen] = useState<boolean>(false);

  const { existingLanguageList, newLanguageList } = useMemo(() => {
    const { existingLanguageList, newLanguageList } = data;

    return {
      existingLanguageList: [...existingLanguageList].sort((a, b) => a.name.localeCompare(b.name)),
      newLanguageList: [...newLanguageList].sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [data]);

  const handleAddLanguage = () => {
    if (hasChanges) {
      onAddClick();
    } else {
      setIsAddNewLanguageDialogOpen(true);
    }
  };

  const handleCloseAddLanguage = () => setIsAddNewLanguageDialogOpen(false);

  return (
    <Box className={styles.selectAdd}>
      <Box className={styles.filterContainer}>
        <Tooltip
          arrow
          placement="bottom"
          title={hasChanges ? getTranslation('CvaProj.SeveralChangesToSave') : EMPTY_STRING}
        >
          <span>
            <Filter
              control={control}
              name="language"
              options={convertToOptions({ items: existingLanguageList, valueKey: 'id', labelKey: 'name' })}
              isDisabled={hasChanges}
            />
          </span>
        </Tooltip>
      </Box>
      <Button variant="outlined" size="medium" onClick={handleAddLanguage}>
        <PlusIcon width={18} />
        {getTranslation('CvaInput.SegPage22', 'addLanguage.language')}
      </Button>
      <TooltipIcon title={getTranslation('CvaProj.AddLanguage')} />
      <AddLanguageDialog
        isOpen={isAddNewLanguageDialogOpen}
        handleClose={handleCloseAddLanguage}
        options={convertToOptions({ items: newLanguageList, valueKey: 'id', labelKey: 'name' })}
      />
    </Box>
  );
};
